import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { ArrowRight, ShieldCheck, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ThemeToggle } from "@/components/theme-toggle"
import { LanguageSwitcher } from "@/components/language-switcher"
import { useAuth, formatApiError } from "@/components/auth-provider"
import { useBrand } from "@/hooks/use-brand"
import { PageSEO } from "@/components/page-seo"
import { API_BASE } from "@/config/api"

const AUTH_BG = "https://images.unsplash.com/photo-1585821569995-86966667ca62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB0aGVyYXBpc3QlMjBvZmZpY2UlMjBpbnRlcmlvciUyMGNhbG18ZW58MHx8fHwxNzc4NzczNTkxfDA&ixlib=rb-4.1.0&q=85"

export default function AuthPage({ mode = "login" }) {
  const { t } = useTranslation()
  const brand = useBrand()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, register, user, acceptInvite } = useAuth()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get("invite")
  const isLogin = mode === "login" && !inviteToken

  const [name, setName] = useState("")
  const [practiceName, setPracticeName] = useState("")
  const isDev = process.env.NODE_ENV === "development"
  const [email, setEmail] = useState(isLogin && isDev ? "demo@asistentpersonal.ro" : "")
  const [password, setPassword] = useState(isLogin && isDev ? "demo1234" : "")
  const [totpCode, setTotpCode] = useState("")
  const [needsTotp, setNeedsTotp] = useState(false)
  const [inviteInfo, setInviteInfo] = useState(null)
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const redirectTo = location.state?.from || "/app"

  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true })
  }, [user, navigate, redirectTo])

  useEffect(() => {
    if (!inviteToken) return
    axios
      .get(`${API_BASE}/auth/invite/${inviteToken}`)
      .then((r) => {
        setInviteInfo(r.data)
        setEmail(r.data.email)
        setName(r.data.name || "")
      })
      .catch(() => setError(t("auth.inviteInvalid")))
  }, [inviteToken, t])

  const heading = inviteToken
    ? t("auth.acceptInvite")
    : isLogin
      ? needsTotp
        ? t("auth.totpTitle")
        : t("auth.welcomeBack")
      : t("auth.createWorkspace")

  const subtitle = inviteToken && inviteInfo
    ? t("auth.joinPractice", { practice: inviteInfo.practice_name })
    : isLogin
      ? needsTotp
        ? t("auth.totpHint")
        : t("auth.signInSubtitle")
      : t("auth.signupSubtitle")

  const submitLabel = submitting
    ? t("auth.pleaseWait")
    : inviteToken
      ? t("auth.acceptInvite")
      : needsTotp
        ? t("auth.verifyCode")
        : isLogin
          ? t("auth.signIn")
          : t("auth.createAccount")

  const onSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSubmitting(true)
    try {
      if (inviteToken) {
        if (password.length < 8) throw new Error(t("auth.passwordMin"))
        await acceptInvite(inviteToken, { password, name: name || inviteInfo?.name })
        navigate(redirectTo, { replace: true })
        return
      }
      if (isLogin) {
        const res = await login(email, password, needsTotp ? totpCode : undefined)
        if (res?.requires_totp) {
          setNeedsTotp(true)
          setSubmitting(false)
          return
        }
      } else {
        if (password.length < 8) throw new Error(t("auth.passwordMin"))
        await register({ name, email, password, practice_name: practiceName || undefined })
      }
      navigate(redirectTo, { replace: true })
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(formatApiError(detail, err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageSEO seoKey="home" noindex />
      <div className="min-h-screen bg-background grid lg:grid-cols-2">
        <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="auth-brand-link">
              <span className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-bold text-sm">A</span>
              <span className="font-display text-base sm:text-lg tracking-tight">{brand.brand}</span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>

          <div className="flex-1 flex items-center">
            <div className="w-full max-w-sm mx-auto">
              <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium">{heading}</h1>
              <p className="text-sm text-muted-foreground mt-2">{subtitle}</p>

              {isLogin && isDev && (
                <div className="mt-6 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground" data-testid="demo-hint">
                  <span className="font-medium text-foreground">{t("auth.tryDemo")}</span>:{" "}
                  <span className="font-mono">demo@asistentpersonal.ro</span> · <span className="font-mono">demo1234</span>
                </div>
              )}

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="auth-form">
                {needsTotp && (
                  <div>
                    <Label htmlFor="totp">{t("auth.totpLabel")}</Label>
                    <Input
                      id="totp"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      className="mt-1.5 rounded-md font-mono"
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                )}
                {(inviteToken || !isLogin) && !needsTotp && (
                  <>
                    <div>
                      <Label htmlFor="name">{t("auth.name")}</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required={!inviteToken}
                        placeholder={t("auth.placeholders.name")}
                        className="mt-1.5 rounded-md"
                        data-testid="auth-name"
                      />
                    </div>
                    {!inviteToken && (
                      <div>
                        <Label htmlFor="practice">{t("auth.practiceName")}</Label>
                        <Input
                          id="practice"
                          value={practiceName}
                          onChange={(e) => setPracticeName(e.target.value)}
                          placeholder={t("auth.placeholders.practice")}
                          className="mt-1.5 rounded-md"
                          data-testid="auth-practice"
                        />
                      </div>
                    )}
                  </>
                )}
                <div>
                  <Label htmlFor="email">{t("auth.workEmail")}</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.placeholders.email")}
                    className="mt-1.5 rounded-md"
                    data-testid="auth-email"
                    disabled={!!inviteToken}
                  />
                </div>
                <div>
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1.5 rounded-md"
                    data-testid="auth-password"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 text-sm text-destructive rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2" data-testid="auth-error">
                    <AlertCircle strokeWidth={1.5} className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button type="submit" size="lg" className="w-full rounded-full" disabled={submitting} data-testid="auth-submit">
                  {submitLabel}
                  {!submitting && <ArrowRight strokeWidth={1.5} className="ml-1 h-4 w-4" />}
                </Button>
              </form>

              <p className="text-sm text-muted-foreground mt-8">
                {isLogin ? (
                  <>
                    {t("auth.noAccount")}{" "}
                    <Link to="/signup" className="text-foreground hover:text-accent">
                      {t("auth.getStarted")}
                    </Link>
                  </>
                ) : (
                  <>
                    {t("auth.hasAccount")}{" "}
                    <Link to="/login" className="text-foreground hover:text-accent">
                      {t("auth.signIn")}
                    </Link>
                  </>
                )}
              </p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <ShieldCheck strokeWidth={1.5} className="h-3.5 w-3.5" /> {t("auth.compliance")}
          </p>
        </div>

        <div className="hidden lg:block relative overflow-hidden">
          <img src={AUTH_BG} alt={t("auth.panelImageAlt")} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
          <div className="absolute bottom-10 left-10 right-10">
            <p className="font-display text-3xl tracking-tighter leading-tight max-w-md">{t("auth.panelQuote")}</p>
            <p className="text-sm text-muted-foreground mt-3">{t("auth.panelRole")}</p>
          </div>
        </div>
      </div>
    </>
  )
}
